// src/cockpit4/components/StrategyMap.tsx
// ============================================
// 战略地图（P1-功能6）- v2.0 详细改进计划
//
// 设计：基于平衡计分卡四维度可视化展示企业战略全景
//   1. 四象限布局（2x2 grid）：
//      - 财务维度：收入增长、成本优化、利润率
//      - 客户维度：客户获取、客户留存、客户满意度
//      - 内部流程维度：运营效率、质量控制、创新能力
//      - 学习成长维度：人才发展、知识管理、技术能力
//   2. 每个战略主题是一个卡片：标题、进度、状态、关联 OKR
//   3. 支持新增/编辑/删除战略主题（弹窗表单）
//   4. 点击卡片展开详情（关联的 OKR 和项目）
//   5. AI 战略执行评估报告生成（一键生成，写入 Vault：11_Strategy/战略评估/{date}.md）
//
// 数据来源：localStorage 持久化（key: xeaos_strategy_map），无 mock 数据，初始为空让用户自建
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Map,
  Plus,
  Loader2,
  RefreshCw,
  Pencil,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  X,
  FileText,
  TrendingUp,
  DollarSign,
  Users,
  Settings,
  GraduationCap,
  Save,
  ChevronRight,
  Target,
} from "lucide-react";

// ============================================
// 类型定义
// ============================================

/** 战略维度（平衡计分卡四维度） */
type StrategyDimension = "finance" | "customer" | "process" | "learning";

/** 主题状态 */
type ThemeStatus = "on_track" | "at_risk" | "completed" | "delayed";

/** 战略主题 */
interface StrategyTheme {
  /** 唯一 ID */
  id: string;
  /** 标题 */
  title: string;
  /** 所属维度 */
  dimension: StrategyDimension;
  /** 描述 */
  description?: string;
  /** 负责人 */
  owner: string;
  /** 状态 */
  status: ThemeStatus;
  /** 进度 0-100 */
  progress: number;
  /** 关联 OKR ID 列表（仅记录 ID，用于关联展示） */
  linkedOKRIds: string[];
  /** 关联 OKR 标题（用于直接展示，避免跨组件读取） */
  linkedOKRTitles: string[];
  /** 关联项目名 */
  linkedProjects: string[];
  /** 关键里程碑 */
  milestones: { name: string; dueDate: string; done: boolean }[];
  /** 创建时间 */
  createdAt: string;
  /** 更新时间 */
  updatedAt: string;
}

/** 战略地图数据 */
interface StrategyMapData {
  themes: StrategyTheme[];
}

// ============================================
// 常量配置
// ============================================

const STORAGE_KEY = "xeaos_strategy_map";

const DIMENSION_CONFIG: Record<
  StrategyDimension,
  { label: string; color: string; icon: typeof DollarSign; description: string }
> = {
  finance: {
    label: "财务维度",
    color: "#10B981",
    icon: DollarSign,
    description: "收入增长 · 成本优化 · 利润率",
  },
  customer: {
    label: "客户维度",
    color: "#3B82F6",
    icon: Users,
    description: "客户获取 · 客户留存 · 客户满意度",
  },
  process: {
    label: "内部流程维度",
    color: "#EC4899",
    icon: Settings,
    description: "运营效率 · 质量控制 · 创新能力",
  },
  learning: {
    label: "学习成长维度",
    color: "#A855F7",
    icon: GraduationCap,
    description: "人才发展 · 知识管理 · 技术能力",
  },
};

const STATUS_CONFIG: Record<ThemeStatus, { label: string; color: string }> = {
  on_track: { label: "正常推进", color: "#3B82F6" },
  at_risk: { label: "风险", color: "#F59E0B" },
  completed: { label: "已完成", color: "#10B981" },
  delayed: { label: "延期", color: "#EF4444" },
};

// ============================================
// 工具函数
// ============================================

function genId(): string {
  return `strat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// ============================================
// 主组件
// ============================================

export default function StrategyMap() {
  const [data, setData] = useState<StrategyMapData>({ themes: [] });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);
  const [editingTheme, setEditingTheme] = useState<{ theme: StrategyTheme | null } | null>(null);
  const [expandedThemeId, setExpandedThemeId] = useState<string | null>(null);
  const [reportContent, setReportContent] = useState<string>("");
  const [showReport, setShowReport] = useState(false);

  // ============================================
  // 数据加载 / 持久化（localStorage）
  // ============================================
  const loadFromStorage = useCallback(() => {
    setLoading(true);
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as StrategyMapData;
        setData(parsed);
      } else {
        setData({ themes: [] });
      }
    } catch {
      setData({ themes: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  const saveToStorage = useCallback((d: StrategyMapData) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(d));
    } catch {
      // 存储失败非阻塞
    }
  }, []);

  useEffect(() => {
    loadFromStorage();
  }, [loadFromStorage]);

  const updateData = useCallback(
    (updater: (prev: StrategyMapData) => StrategyMapData) => {
      setData((prev) => {
        const next = updater(prev);
        saveToStorage(next);
        return next;
      });
    },
    [saveToStorage]
  );

  // ============================================
  // 节点操作
  // ============================================
  const handleSaveTheme = useCallback((theme: StrategyTheme) => {
    updateData((prev) => {
      const existing = prev.themes.find((t) => t.id === theme.id);
      if (existing) {
        return {
          themes: prev.themes.map((t) =>
            t.id === theme.id ? { ...theme, updatedAt: new Date().toISOString() } : t
          ),
        };
      }
      return {
        themes: [...prev.themes, { ...theme, id: genId(), createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }],
      };
    });
    setEditingTheme(null);
    setMessage({ type: "ok", text: "✅ 战略主题已保存（已持久化到 localStorage）" });
  }, [updateData]);

  const handleDeleteTheme = useCallback((id: string) => {
    if (!confirm("确定删除该战略主题？此操作不可撤销。")) return;
    updateData((prev) => ({ themes: prev.themes.filter((t) => t.id !== id) }));
    setMessage({ type: "ok", text: "✅ 战略主题已删除" });
  }, [updateData]);

  // ============================================
  // 生成 AI 战略执行评估报告
  // 持久化到 Vault：11_Strategy/战略评估/{date}.md
  // ============================================
  const generateAssessment = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const date = new Date().toISOString().slice(0, 10);
      const themes = data.themes;

      // 按维度分组
      const byDimension: Record<StrategyDimension, StrategyTheme[]> = {
        finance: themes.filter((t) => t.dimension === "finance"),
        customer: themes.filter((t) => t.dimension === "customer"),
        process: themes.filter((t) => t.dimension === "process"),
        learning: themes.filter((t) => t.dimension === "learning"),
      };

      // 整体执行率
      const avgProgress = themes.length > 0
        ? Math.round(themes.reduce((s, t) => s + t.progress, 0) / themes.length)
        : 0;
      const onTrack = themes.filter((t) => t.status === "on_track").length;
      const atRisk = themes.filter((t) => t.status === "at_risk").length;
      const delayed = themes.filter((t) => t.status === "delayed").length;
      const completed = themes.filter((t) => t.status === "completed").length;

      // 各维度平均进度
      const dimensionScores: Record<StrategyDimension, number> = {
        finance: byDimension.finance.length > 0
          ? Math.round(byDimension.finance.reduce((s, t) => s + t.progress, 0) / byDimension.finance.length)
          : 0,
        customer: byDimension.customer.length > 0
          ? Math.round(byDimension.customer.reduce((s, t) => s + t.progress, 0) / byDimension.customer.length)
          : 0,
        process: byDimension.process.length > 0
          ? Math.round(byDimension.process.reduce((s, t) => s + t.progress, 0) / byDimension.process.length)
          : 0,
        learning: byDimension.learning.length > 0
          ? Math.round(byDimension.learning.reduce((s, t) => s + t.progress, 0) / byDimension.learning.length)
          : 0,
      };

      const content = [
        `---`,
        `type: strategy_assessment`,
        `date: ${date}`,
        `created_at: ${new Date().toISOString()}`,
        `total_themes: ${themes.length}`,
        `avg_progress: ${avgProgress}`,
        `completed: ${completed}`,
        `at_risk: ${atRisk}`,
        `delayed: ${delayed}`,
        `---`,
        ``,
        `# 🗺️ 战略执行评估报告 · ${date}`,
        ``,
        `> 生成时间：${new Date().toLocaleString("zh-CN", { hour12: false })}`,
        ``,
        `## 一、整体执行情况`,
        ``,
        `- 战略主题总数：${themes.length}`,
        `- 整体平均进度：${avgProgress}%`,
        `- 正常推进：${onTrack} 项`,
        `- 已完成：${completed} 项`,
        `- 风险项：${atRisk} 项`,
        `- 延期项：${delayed} 项`,
        ``,
        `## 二、四维度执行评估`,
        ``,
        ...(Object.keys(DIMENSION_CONFIG) as StrategyDimension[]).map((dim) => {
          const cfg = DIMENSION_CONFIG[dim];
          const dimThemes = byDimension[dim];
          const score = dimensionScores[dim];
          return `### ${cfg.label}（${cfg.description}）\n- 主题数：${dimThemes.length}\n- 平均进度：${score}%\n- 评估：${
            score >= 80 ? "执行良好，按计划推进" :
            score >= 60 ? "执行平稳，需关注细节" :
            score >= 30 ? "执行滞后，需加强推进" :
            "严重滞后，需重新评估"
          }`;
        }),
        ``,
        `## 三、风险主题与改进建议`,
        ``,
        ...(themes.filter((t) => t.status === "at_risk" || t.status === "delayed").length > 0
          ? themes
              .filter((t) => t.status === "at_risk" || t.status === "delayed")
              .map((t) => `- ⚠️ **${t.title}**（${DIMENSION_CONFIG[t.dimension].label}）— 进度 ${t.progress}%，状态：${STATUS_CONFIG[t.status].label}，负责人：${t.owner}`)
          : ["- 暂无风险/延期主题"]),
        ``,
        `## 四、亮点主题`,
        ``,
        ...(themes.filter((t) => t.status === "completed").length > 0
          ? themes
              .filter((t) => t.status === "completed")
              .map((t) => `- ✅ **${t.title}**（${DIMENSION_CONFIG[t.dimension].label}）— 已完成，负责人：${t.owner}`)
          : ["- 暂无已完成主题"]),
        ``,
        `## 五、整体建议`,
        ``,
        avgProgress >= 80
          ? `战略执行整体良好，平均进度 ${avgProgress}%，建议继续保持当前节奏，重点关注 ${atRisk + delayed} 个风险/延期主题。`
          : avgProgress >= 50
          ? `战略执行平稳，平均进度 ${avgProgress}%，但有 ${atRisk + delayed} 个主题存在风险或延期，建议优先解决阻塞问题。`
          : `战略执行严重滞后，平均进度仅 ${avgProgress}%，需立即召开战略复盘会议，重新评估目标可行性和资源分配。`,
        ``,
        `---`,
        `评估人：CEO Workspace · Strategy Map`,
      ].join("\n");

      setReportContent(content);
      setShowReport(true);

      // 写入 Vault
      await fetch(
        `/vault-api/write?path=${encodeURIComponent(`11_Strategy/战略评估/${date}.md`)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content }),
        }
      );

      setMessage({
        type: "ok",
        text: `✅ 战略评估报告已生成并写入 Vault：11_Strategy/战略评估/${date}.md`,
      });
    } catch (err) {
      setMessage({
        type: "err",
        text: `❌ 评估报告生成失败：${(err as Error).message}`,
      });
    } finally {
      setLoading(false);
    }
  }, [data]);

  // ============================================
  // 统计
  // ============================================
  const stats = useMemo(() => {
    const themes = data.themes;
    return {
      total: themes.length,
      onTrack: themes.filter((t) => t.status === "on_track").length,
      atRisk: themes.filter((t) => t.status === "at_risk").length,
      delayed: themes.filter((t) => t.status === "delayed").length,
      completed: themes.filter((t) => t.status === "completed").length,
      avgProgress: themes.length > 0
        ? Math.round(themes.reduce((s, t) => s + t.progress, 0) / themes.length)
        : 0,
    };
  }, [data]);

  // 按维度分组
  const themesByDimension = useMemo(() => {
    const map: Record<StrategyDimension, StrategyTheme[]> = {
      finance: [],
      customer: [],
      process: [],
      learning: [],
    };
    data.themes.forEach((t) => map[t.dimension].push(t));
    return map;
  }, [data]);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/40 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
            <Map size={13} className="text-amber-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">战略地图</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Strategy Map · 总 {stats.total} · 正常 {stats.onTrack} · 风险 {stats.atRisk} · 延期 {stats.delayed} · 平均 {stats.avgProgress}%
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* 生成评估报告 */}
          <button
            onClick={generateAssessment}
            disabled={loading || stats.total === 0}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-500/15 border border-amber-500/30 hover:bg-amber-500/25 transition-colors disabled:opacity-50"
            title="生成 AI 战略执行评估报告"
          >
            <FileText size={10} className="text-amber-400" />
            <span className="text-[9px] font-mono text-amber-300 uppercase tracking-wider">评估报告</span>
          </button>
          {/* 新建主题 */}
          <button
            onClick={() => setEditingTheme({ theme: null })}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/15 border border-amber-500/30 hover:bg-amber-500/25 transition-colors"
            title="新建战略主题"
          >
            {loading ? (
              <Loader2 size={11} className="text-amber-400 animate-spin" />
            ) : (
              <Plus size={11} className="text-amber-400" />
            )}
            <span className="text-[9px] font-mono text-amber-300 uppercase tracking-wider">新建主题</span>
          </button>
          <button
            onClick={loadFromStorage}
            disabled={loading}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.08] transition-colors disabled:opacity-50"
          >
            <RefreshCw size={11} className="text-gray-400" />
          </button>
        </div>
      </div>

      {/* 消息提示 */}
      {message && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div
            className={`px-3 py-2 rounded-md border text-[10px] font-mono whitespace-pre-line ${
              message.type === "ok"
                ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300"
                : "border-red-500/30 bg-red-500/[0.06] text-red-300"
            }`}
          >
            {message.text}
          </div>
        </div>
      )}

      {/* 内容区：四象限布局 */}
      <div className="p-4">
        {data.themes.length === 0 ? (
          <div className="py-12 text-center">
            <Map size={28} className="mx-auto text-gray-700 mb-3" />
            <p className="text-[12px] font-mono text-gray-500 mb-2">暂无战略主题</p>
            <p className="text-[10px] font-mono text-gray-600">点击右上角「新建主题」创建第一个战略主题</p>
            <p className="text-[9px] font-mono text-gray-700 mt-3">
              基于平衡计分卡四维度：财务 · 客户 · 内部流程 · 学习成长
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {(Object.keys(DIMENSION_CONFIG) as StrategyDimension[]).map((dim) => {
              const cfg = DIMENSION_CONFIG[dim];
              const dimThemes = themesByDimension[dim];
              const DimIcon = cfg.icon;
              const dimAvg = dimThemes.length > 0
                ? Math.round(dimThemes.reduce((s, t) => s + t.progress, 0) / dimThemes.length)
                : 0;
              return (
                <div
                  key={dim}
                  className="rounded-lg border border-white/[0.04] bg-white/[0.02] p-3"
                  style={{ borderColor: `${cfg.color}20` }}
                >
                  {/* 维度标题 */}
                  <div className="flex items-center justify-between mb-2 pb-2 border-b border-white/[0.04]">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-5 h-5 rounded flex items-center justify-center"
                        style={{ background: `${cfg.color}15` }}
                      >
                        <DimIcon size={11} style={{ color: cfg.color }} />
                      </div>
                      <div>
                        <div className="text-[12px] font-semibold text-white">{cfg.label}</div>
                        <div className="text-[8px] font-mono text-gray-600">{cfg.description}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono font-bold" style={{ color: cfg.color }}>
                        {dimAvg}%
                      </span>
                      <button
                        onClick={() => setEditingTheme({ theme: null })}
                        className="p-1 rounded hover:bg-white/[0.08] transition-colors"
                        title={`在${cfg.label}下新建主题`}
                        // 通过自定义事件传递维度，简化为打开默认表单后用户选择
                      >
                        <Plus size={10} className="text-gray-500" />
                      </button>
                    </div>
                  </div>

                  {/* 主题列表 */}
                  <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1">
                    {dimThemes.length === 0 ? (
                      <div className="text-center py-4 text-[10px] font-mono text-gray-600">
                        暂无主题
                      </div>
                    ) : (
                      dimThemes.map((theme, idx) => (
                        <StrategyThemeCard
                          key={theme.id}
                          theme={theme}
                          expanded={expandedThemeId === theme.id}
                          onToggle={() =>
                            setExpandedThemeId(expandedThemeId === theme.id ? null : theme.id)
                          }
                          onEdit={() => setEditingTheme({ theme })}
                          onDelete={() => handleDeleteTheme(theme.id)}
                        />
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 编辑表单 Modal */}
      <AnimatePresence>
        {editingTheme && (
          <StrategyThemeEditDialog
            initial={editingTheme.theme}
            onClose={() => setEditingTheme(null)}
            onSave={handleSaveTheme}
          />
        )}
      </AnimatePresence>

      {/* 评估报告预览 Modal */}
      <AnimatePresence>
        {showReport && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowReport(false)}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-2xl rounded-xl border border-white/[0.08] bg-[#0a0e1a] overflow-hidden flex flex-col max-h-[80vh]"
            >
              <div className="h-1 bg-gradient-to-r from-amber-400 to-orange-500" />
              <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-amber-400" />
                  <h3 className="text-[13px] font-semibold text-white">战略执行评估报告</h3>
                </div>
                <button
                  onClick={() => setShowReport(false)}
                  className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05]"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <pre className="text-[11px] font-mono text-gray-300 whitespace-pre-wrap leading-relaxed">
                  {reportContent}
                </pre>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// 子组件：战略主题卡片
// ============================================

interface StrategyThemeCardProps {
  theme: StrategyTheme;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function StrategyThemeCard({ theme, expanded, onToggle, onEdit, onDelete }: StrategyThemeCardProps) {
  const dimCfg = DIMENSION_CONFIG[theme.dimension];
  const statusCfg = STATUS_CONFIG[theme.status];
  const isRisk = theme.status === "at_risk" || theme.status === "delayed";
  const isCompleted = theme.status === "completed";

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-md border p-2 transition-all ${
        isRisk
          ? "border-red-500/30 bg-red-500/[0.04]"
          : isCompleted
          ? "border-emerald-500/20 bg-emerald-500/[0.03]"
          : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]"
      }`}
    >
      <div className="flex items-center gap-2">
        {/* 展开按钮 */}
        <button
          onClick={onToggle}
          className="flex-shrink-0 w-4 h-4 rounded flex items-center justify-center hover:bg-white/[0.08] transition-colors"
        >
          <ChevronRight
            size={10}
            className={`text-gray-400 transition-transform ${expanded ? "rotate-90" : ""}`}
          />
        </button>

        {/* 标题 */}
        <span className={`text-[11px] truncate flex-1 ${isCompleted ? "text-gray-400 line-through" : "text-gray-200"}`}>
          {theme.title}
        </span>

        {/* 负责人 */}
        {theme.owner && (
          <span className="text-[9px] font-mono text-gray-500 flex-shrink-0">@ {theme.owner}</span>
        )}

        {/* 进度 */}
        <div className="flex items-center gap-1 flex-shrink-0 w-20">
          <div className="flex-1 h-1 rounded-full bg-white/[0.05] overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${theme.progress}%`,
                background: isRisk ? statusCfg.color : isCompleted ? statusCfg.color : dimCfg.color,
              }}
            />
          </div>
          <span
            className="text-[8px] font-mono font-bold w-7 text-right"
            style={{ color: isRisk ? statusCfg.color : isCompleted ? statusCfg.color : dimCfg.color }}
          >
            {theme.progress}%
          </span>
        </div>

        {/* 状态 */}
        <span
          className="flex-shrink-0 px-1 py-0.5 rounded text-[8px] font-mono uppercase flex items-center gap-0.5"
          style={{ background: `${statusCfg.color}15`, color: statusCfg.color }}
        >
          {isRisk && <AlertTriangle size={7} />}
          {isCompleted && <CheckCircle2 size={7} />}
          {statusCfg.label}
        </span>

        {/* 操作 */}
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            onClick={onEdit}
            className="p-0.5 rounded hover:bg-white/[0.08] transition-colors"
            title="编辑"
          >
            <Pencil size={9} className="text-gray-500 hover:text-cyan-300" />
          </button>
          <button
            onClick={onDelete}
            className="p-0.5 rounded hover:bg-white/[0.08] transition-colors"
            title="删除"
          >
            <Trash2 size={9} className="text-gray-500 hover:text-red-400" />
          </button>
        </div>
      </div>

      {/* 展开详情 */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-2 pt-2 border-t border-white/[0.04] space-y-1.5 text-[10px] font-mono">
              {theme.description && (
                <div className="text-gray-400">
                  <span className="text-gray-600">描述：</span>
                  {theme.description}
                </div>
              )}
              {theme.linkedOKRTitles.length > 0 && (
                <div>
                  <span className="text-gray-600">关联 OKR：</span>
                  <div className="mt-0.5 pl-2 space-y-0.5">
                    {theme.linkedOKRTitles.map((title, i) => (
                      <div key={i} className="flex items-center gap-1 text-gray-300">
                        <Target size={8} style={{ color: dimCfg.color }} />
                        {title}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {theme.linkedProjects.length > 0 && (
                <div>
                  <span className="text-gray-600">关联项目：</span>
                  <span className="text-gray-300">{theme.linkedProjects.join("、")}</span>
                </div>
              )}
              {theme.milestones.length > 0 && (
                <div>
                  <span className="text-gray-600">关键里程碑：</span>
                  <div className="mt-0.5 pl-2 space-y-0.5">
                    {theme.milestones.map((m, i) => (
                      <div key={i} className="flex items-center gap-1">
                        {m.done ? (
                          <CheckCircle2 size={8} className="text-emerald-400" />
                        ) : (
                          <span className="w-2 h-2 rounded-full border border-gray-600" />
                        )}
                        <span className={m.done ? "text-gray-400 line-through" : "text-gray-300"}>
                          {m.name}
                        </span>
                        {m.dueDate && <span className="text-gray-600">· {m.dueDate}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {theme.linkedOKRTitles.length === 0 &&
                theme.linkedProjects.length === 0 &&
                theme.milestones.length === 0 &&
                !theme.description && (
                  <div className="text-gray-600 italic">暂无详细信息，点击编辑补充</div>
                )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// 子组件：战略主题编辑表单 Modal
// ============================================

interface StrategyThemeEditDialogProps {
  initial: StrategyTheme | null;
  onClose: () => void;
  onSave: (theme: StrategyTheme) => void;
}

function StrategyThemeEditDialog({ initial, onClose, onSave }: StrategyThemeEditDialogProps) {
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [dimension, setDimension] = useState<StrategyDimension>(initial?.dimension || "finance");
  const [owner, setOwner] = useState(initial?.owner || "");
  const [status, setStatus] = useState<ThemeStatus>(initial?.status || "on_track");
  const [progress, setProgress] = useState(initial?.progress.toString() || "0");
  const [linkedOKRTitles, setLinkedOKRTitles] = useState<string>(
    (initial?.linkedOKRTitles || []).join("\n")
  );
  const [linkedProjects, setLinkedProjects] = useState<string>(
    (initial?.linkedProjects || []).join("、")
  );
  const [milestonesText, setMilestonesText] = useState<string>(
    (initial?.milestones || [])
      .map((m) => `${m.done ? "[x]" : "[ ]"} ${m.name}${m.dueDate ? ` | ${m.dueDate}` : ""}`)
      .join("\n")
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    // 解析里程碑文本：[x] 名称 | 日期 或 [ ] 名称 | 日期
    const milestones = milestonesText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const m = line.match(/^\[(x| )\]\s*(.+?)(?:\s*\|\s*(.+))?$/i);
        if (m) {
          return {
            name: m[2].trim(),
            dueDate: m[3]?.trim() || "",
            done: m[1].toLowerCase() === "x",
          };
        }
        return { name: line, dueDate: "", done: false };
      });

    const theme: StrategyTheme = {
      id: initial?.id || genId(),
      title: title.trim(),
      description: description.trim() || undefined,
      dimension,
      owner: owner.trim() || "未指派",
      status,
      progress: Math.min(100, Math.max(0, parseInt(progress) || 0)),
      linkedOKRIds: initial?.linkedOKRIds || [],
      linkedOKRTitles: linkedOKRTitles
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
      linkedProjects: linkedProjects
        .split(/[、,，]/)
        .map((s) => s.trim())
        .filter(Boolean),
      milestones,
      createdAt: initial?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    onSave(theme);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-6"
    >
      <motion.div
        initial={{ scale: 0.95, y: 10 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 10 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg rounded-xl border border-white/[0.08] bg-[#0a0e1a] overflow-hidden"
      >
        <div className="h-1 bg-gradient-to-r from-amber-400 to-orange-500" />
        <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <Map size={14} className="text-amber-400" />
            <h3 className="text-[13px] font-semibold text-white">
              {initial ? "编辑" : "新建"} 战略主题
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05]"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3 max-h-[75vh] overflow-y-auto">
          {/* 标题 */}
          <div>
            <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
              标题 *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              required
              placeholder="战略主题标题"
              className="w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/[0.08] text-[12px] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-cyan-500/40"
            />
          </div>

          {/* 维度 + 负责人 */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
                维度
              </label>
              <select
                value={dimension}
                onChange={(e) => setDimension(e.target.value as StrategyDimension)}
                className="w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/[0.08] text-[12px] text-gray-200 focus:outline-none focus:border-cyan-500/40"
              >
                {(Object.keys(DIMENSION_CONFIG) as StrategyDimension[]).map((d) => (
                  <option key={d} value={d}>
                    {DIMENSION_CONFIG[d].label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
                负责人
              </label>
              <input
                type="text"
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                placeholder="AI 岗位或人名"
                className="w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/[0.08] text-[12px] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-cyan-500/40"
              />
            </div>
          </div>

          {/* 描述 */}
          <div>
            <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
              描述
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="战略主题详细描述"
              className="w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/[0.08] text-[12px] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-cyan-500/40 resize-none"
            />
          </div>

          {/* 进度 + 状态 */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
                进度：{progress}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={progress}
                onChange={(e) => setProgress(e.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
                状态
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as ThemeStatus)}
                className="w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/[0.08] text-[12px] text-gray-200 focus:outline-none focus:border-cyan-500/40"
              >
                <option value="on_track">正常推进</option>
                <option value="at_risk">风险</option>
                <option value="completed">已完成</option>
                <option value="delayed">延期</option>
              </select>
            </div>
          </div>

          {/* 关联 OKR */}
          <div>
            <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
              关联 OKR（每行一个）
            </label>
            <textarea
              value={linkedOKRTitles}
              onChange={(e) => setLinkedOKRTitles(e.target.value)}
              rows={2}
              placeholder={"例如：\n提升公众号粉丝量到 1万\n季度收入达到 10万"}
              className="w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/[0.08] text-[12px] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-cyan-500/40 resize-none"
            />
          </div>

          {/* 关联项目 */}
          <div>
            <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
              关联项目（用顿号分隔）
            </label>
            <input
              type="text"
              value={linkedProjects}
              onChange={(e) => setLinkedProjects(e.target.value)}
              placeholder="例如：酒店数字化系统、AI 命运预测"
              className="w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/[0.08] text-[12px] text-gray-200 placeholder-gray-600 focus:outline-none focus:border-cyan-500/40"
            />
          </div>

          {/* 里程碑 */}
          <div>
            <label className="block text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
              里程碑（每行一个，格式：[x] 名称 | 日期 或 [ ] 名称 | 日期）
            </label>
            <textarea
              value={milestonesText}
              onChange={(e) => setMilestonesText(e.target.value)}
              rows={3}
              placeholder={"[ ] 完成市场调研 | 2026-08-15\n[x] 立项 | 2026-07-01"}
              className="w-full px-3 py-2 rounded-md bg-white/[0.03] border border-white/[0.08] text-[11px] font-mono text-gray-200 placeholder-gray-600 focus:outline-none focus:border-cyan-500/40 resize-none"
            />
          </div>

          {/* 操作按钮 */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/[0.06]">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-[11px] font-mono text-gray-400 hover:text-white bg-white/[0.04] hover:bg-white/[0.08] rounded transition-colors"
            >
              取消
            </button>
            <button
              type="submit"
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-mono text-white rounded transition-colors bg-amber-500/20 border border-amber-500/40"
            >
              <Save size={11} />
              保存
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
